import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import { ChevronRight, ChevronLeft, FlaskConical } from 'lucide-react';

interface LabStory {
  id: string;
  number: string;
  title: string;
  path: string;
  accent: string;
}

const labStories: LabStory[] = [
  { id: '100-lines', number: '01', title: '100 Lines of Code', path: '/lab/100-lines', accent: '#fbbf24' },
  { id: 'founding-engineer', number: '02', title: 'First Commit to Million Lives', path: '/lab/founding-engineer', accent: '#fb923c' },
  { id: 'mcp-jest', number: '03', title: 'MCP-Jest', path: '/lab/mcp-jest', accent: '#22d3ee' },
  { id: 'ai-terminal-course', number: '04', title: 'AI Terminal Course', path: '/lab/ai-terminal-course', accent: '#4ade80' },
  { id: 'unengineered', number: '05', title: 'Unengineered', path: '/lab/unengineered', accent: '#f472b6' },
  { id: 'open-source-playbook', number: '06', title: 'Open Source Playbook', path: '/lab/open-source-playbook', accent: '#38bdf8' },
  { id: 'abstraction-stack', number: '07', title: 'The Abstraction Stack', path: '/lab/abstraction-stack', accent: '#c4b5fd' },
  { id: 'meeting-tax', number: '08', title: 'The Meeting Tax', path: '/lab/meeting-tax', accent: '#fca5a5' },
  { id: 'interview-paradox', number: '09', title: 'The Interview Paradox', path: '/lab/interview-paradox', accent: '#3b82f6' },
  { id: 'keyboard-journey', number: '10', title: "The Keyboard's Journey", path: '/lab/keyboard-journey', accent: '#22d3ee' },
  { id: 'learning-ai', number: '11', title: 'Learning AI by Building AI', path: '/lab/learning-ai', accent: '#8b5cf6' },
];

interface LabNavigationProps {
  currentLabId?: string;
  variant?: 'dark' | 'light';
  activeIndex?: number;
  onNavigate?: (index: number) => void;
}

const LabNavigation: React.FC<LabNavigationProps> = ({
  currentLabId,
  variant = 'light',
  activeIndex,
  onNavigate
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isExpanded, setIsExpanded] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsExpanded(false);
      }
    };

    if (isExpanded) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isExpanded]);

  // Determine current lab from either prop or URL
  const currentPath = location.pathname;
  const currentIndex = activeIndex ?? labStories.findIndex(lab => lab.path === currentPath || lab.id === currentLabId);

  const isDark = variant === 'dark';

  const bgClass = isDark
    ? 'bg-white/5 hover:bg-white/10 border-white/10'
    : 'bg-zinc-900/5 hover:bg-zinc-900/10 border-zinc-200';

  const textClass = isDark ? 'text-white' : 'text-zinc-900';
  const mutedClass = isDark ? 'text-white/50' : 'text-zinc-500';
  const hoverBgClass = isDark ? 'hover:bg-white/10' : 'hover:bg-zinc-100';

  const handleLabClick = (index: number, path: string) => {
    if (onNavigate) {
      onNavigate(index);
    } else {
      navigate(path);
    }
  };

  return (
    <div
      ref={containerRef}
      className="fixed z-40 right-4"
      style={{ top: '50%', transform: 'translateY(-50%)' }}
    >
      {/* Toggle Button */}
      <motion.button
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 0.5 }}
        onClick={() => setIsExpanded(!isExpanded)}
        className={`absolute right-0 p-3 rounded-full backdrop-blur-md border transition-all duration-300 ${bgClass} ${textClass}`}
        style={{ top: '50%', transform: 'translateY(-50%)' }}
        aria-label={isExpanded ? "Collapse navigation" : "Expand navigation"}
      >
        <AnimatePresence mode="wait">
          {isExpanded ? (
            <motion.div
              key="close"
              initial={{ rotate: -90, opacity: 0 }}
              animate={{ rotate: 0, opacity: 1 }}
              exit={{ rotate: 90, opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              <ChevronRight className="w-5 h-5" />
            </motion.div>
          ) : (
            <motion.div
              key="open"
              initial={{ rotate: 90, opacity: 0 }}
              animate={{ rotate: 0, opacity: 1 }}
              exit={{ rotate: -90, opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              <FlaskConical className="w-5 h-5" />
            </motion.div>
          )}
        </AnimatePresence>
      </motion.button>

      {/* Navigation Panel */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ opacity: 0, x: 100 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 100 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className={`w-72 max-h-[70vh] overflow-hidden rounded-2xl backdrop-blur-xl border shadow-2xl ${
              isDark ? 'bg-black/80 border-white/10' : 'bg-white/90 border-zinc-200'
            }`}
          >
            {/* Header */}
            <div className={`px-4 py-3 border-b ${isDark ? 'border-white/10' : 'border-zinc-200'}`}>
              <div className="flex items-center gap-2">
                <FlaskConical className={`w-4 h-4 ${mutedClass}`} />
                <span className={`text-xs font-medium uppercase tracking-wider ${mutedClass}`}>
                  Lab Experiments
                </span>
              </div>
            </div>

            {/* Scrollable List */}
            <div className="overflow-y-auto max-h-[calc(70vh-48px)] py-2 px-2">
              {labStories.map((lab, index) => {
                const isActive = index === currentIndex;

                return (
                  <motion.button
                    key={lab.id}
                    onClick={() => handleLabClick(index, lab.path)}
                    whileHover={{ x: 4 }}
                    whileTap={{ scale: 0.98 }}
                    className={`w-full text-left px-3 py-2.5 rounded-xl transition-all duration-200 flex items-center gap-3 group ${
                      isActive
                        ? isDark
                          ? 'bg-white/15'
                          : 'bg-zinc-100'
                        : hoverBgClass
                    }`}
                  >
                    {/* Number Badge */}
                    <span
                      className={`flex-shrink-0 w-7 h-7 rounded-lg flex items-center justify-center text-xs font-bold transition-colors ${
                        isActive
                          ? 'text-black'
                          : isDark
                            ? 'bg-white/10 text-white/60 group-hover:text-white'
                            : 'bg-zinc-200 text-zinc-500 group-hover:text-zinc-700'
                      }`}
                      style={isActive ? { backgroundColor: lab.accent } : undefined}
                    >
                      {lab.number}
                    </span>

                    {/* Title */}
                    <span className={`text-sm font-medium truncate transition-colors ${
                      isActive
                        ? textClass
                        : isDark
                          ? 'text-white/70 group-hover:text-white'
                          : 'text-zinc-600 group-hover:text-zinc-900'
                    }`}>
                      {lab.title}
                    </span>

                    {/* Active Indicator */}
                    {isActive && (
                      <motion.div
                        layoutId="activeLabIndicator"
                        className="ml-auto w-1.5 h-1.5 rounded-full flex-shrink-0"
                        style={{ backgroundColor: lab.accent }}
                      />
                    )}
                  </motion.button>
                );
              })}
            </div>

            {/* Footer with count and navigation */}
            <div className={`px-4 py-3 border-t flex items-center justify-between ${isDark ? 'border-white/10' : 'border-zinc-200'}`}>
              <span className={`text-xs ${mutedClass}`}>
                {currentIndex >= 0 ? `${currentIndex + 1} of ${labStories.length}` : `${labStories.length} experiments`}
              </span>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => {
                    const prevIndex = currentIndex > 0 ? currentIndex - 1 : labStories.length - 1;
                    handleLabClick(prevIndex, labStories[prevIndex].path);
                  }}
                  className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${hoverBgClass} ${mutedClass}`}
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <button
                  onClick={() => {
                    const nextIndex = currentIndex < labStories.length - 1 ? currentIndex + 1 : 0;
                    handleLabClick(nextIndex, labStories[nextIndex].path);
                  }}
                  className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${hoverBgClass} ${mutedClass}`}
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default LabNavigation;
